import React, { useEffect, useMemo, useState } from "react";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { firestore } from "../shared/FirebaseConfig";
import { useUser } from "../shared/UserContext";
import "./LunchCounts.css";

function todayKeyLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const tally = (records, field) => {
  const m = {};
  records.forEach((r) => {
    const v = r[field];
    if (v) m[v] = (m[v] || 0) + 1;
  });
  return m;
};

// Column order: the school's configured options first, then any stragglers seen
// in the data (e.g. an option that was later removed from the menu).
const columnsFor = (configured, totals) => {
  const extra = Object.keys(totals).filter((k) => !configured.includes(k));
  return [...configured, ...extra];
};

// One count matrix: a row per group (class or grade), a column per menu option.
function MatrixTable({ title, options, groups, field, totals }) {
  if (!options.length) return null;
  const grandTotal = options.reduce((s, o) => s + (totals[o] || 0), 0);
  return (
    <div className="lunch-matrix">
      <h3>{title}</h3>
      <div className="lunch-matrix-scroll">
        <table className="table">
          <thead>
            <tr>
              <th>{/* group */}</th>
              {options.map((o) => <th key={o}>{o}</th>)}
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => {
              const counts = g[field];
              const rowTotal = options.reduce((s, o) => s + (counts[o] || 0), 0);
              return (
                <tr key={g.key}>
                  <td className="lunch-rowhead">{g.label}</td>
                  {options.map((o) => (
                    <td key={o} className={counts[o] ? "" : "lunch-zero"}>{counts[o] || "—"}</td>
                  ))}
                  <td className="lunch-rowtotal">{rowTotal}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td className="lunch-rowhead">All</td>
              {options.map((o) => <td key={o}>{totals[o] || "—"}</td>)}
              <td className="lunch-rowtotal">{grandTotal}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

// Lunch tally for today, grouped by class or grade. Reads each class's today
// attendance subcollection; lunch choices were snapshotted with the student's
// grade at check-in, so no roster join is needed.
//
// Admin (no `ownClasses` prop): school-wide across every active class.
// Teacher (`ownClasses` passed): scoped to just their own classes.
export default function LunchCounts({ ownClasses }) {
  const { schoolId, lunchEntrees, lunchDrinks } = useUser();
  const isOwnScope = Array.isArray(ownClasses);
  const [schoolClasses, setSchoolClasses] = useState([]);
  const [lunchByClass, setLunchByClass] = useState({});
  const [groupBy, setGroupBy] = useState("class");
  const todayKey = useMemo(() => todayKeyLocal(), []);

  // Admin scope subscribes to every school class; teacher scope uses the classes
  // passed in (their own) and skips the school-wide query.
  useEffect(() => {
    if (isOwnScope || !schoolId) return;
    return onSnapshot(
      query(collection(firestore, "classes"), where("schoolId", "==", schoolId)),
      (snap) => setSchoolClasses(snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((c) => !c.isArchived)),
      (err) => console.error("[LunchCounts] classes:", err),
    );
  }, [isOwnScope, schoolId]);

  const classes = useMemo(
    () => (isOwnScope ? ownClasses.filter((c) => !c.isArchived) : schoolClasses),
    [isOwnScope, ownClasses, schoolClasses]
  );

  const activeIds = useMemo(() => classes.map((c) => c.id).sort().join(","), [classes]);

  useEffect(() => {
    const ids = activeIds ? activeIds.split(",") : [];
    if (!ids.length) { setLunchByClass({}); return; }
    const unsubs = ids.map((cid) =>
      onSnapshot(
        collection(firestore, "classes", cid, "attendance", todayKey, "students"),
        (snap) => {
          const rows = [];
          snap.forEach((d) => {
            const x = d.data();
            if (x.lunch && (x.lunch.entree || x.lunch.drink)) rows.push({ sid: d.id, ...x.lunch });
          });
          setLunchByClass((prev) => ({ ...prev, [cid]: rows }));
        },
        (err) => console.error("[LunchCounts] attendance:", err),
      )
    );
    return () => unsubs.forEach((fn) => fn());
  }, [activeIds, todayKey]);

  const classNameOf = useMemo(() => {
    const m = {};
    classes.forEach((c) => { m[c.id] = c.className || "Class"; });
    return m;
  }, [classes]);

  const all = useMemo(() => {
    const out = [];
    Object.entries(lunchByClass).forEach(([cid, rows]) => rows.forEach((r) => out.push({ ...r, cid })));
    return out;
  }, [lunchByClass]);

  const overall = useMemo(
    () => ({ entrees: tally(all, "entree"), drinks: tally(all, "drink"), total: all.length }),
    [all]
  );

  const groups = useMemo(() => {
    const m = {};
    all.forEach((r) => {
      const key = groupBy === "class"
        ? (classNameOf[r.cid] || "Class")
        : (r.grade ? `Grade ${r.grade}` : "No grade");
      (m[key] = m[key] || []).push(r);
    });
    return Object.keys(m)
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
      .map((key) => ({ key, label: key, count: m[key].length, entrees: tally(m[key], "entree"), drinks: tally(m[key], "drink") }));
  }, [all, groupBy, classNameOf]);

  const entreeCols = columnsFor(lunchEntrees, overall.entrees);
  const drinkCols = columnsFor(lunchDrinks, overall.drinks);

  return (
    <div style={{ marginTop: "var(--space-3)", width: "100%" }}>
      <div className="att-header">
        <h2 style={{ margin: 0 }}>{isOwnScope ? "My lunch counts · today" : "Lunch counts · today"}</h2>
        <span className="att-summary">{overall.total} order{overall.total !== 1 ? "s" : ""}</span>
      </div>

      <div className="absent-toolbar">
        <div className="absent-seg">
          <button className={groupBy === "class" ? "active" : ""} onClick={() => setGroupBy("class")}>By class</button>
          <button className={groupBy === "grade" ? "active" : ""} onClick={() => setGroupBy("grade")}>By grade</button>
        </div>
      </div>

      {overall.total === 0 ? (
        <p className="att-hint" style={{ marginTop: "var(--space-4)" }}>
          No lunch orders yet today. Choices appear here as teachers check students in.
        </p>
      ) : (
        <>
          <MatrixTable title="Entrées" options={entreeCols} groups={groups} field="entrees" totals={overall.entrees} />
          <MatrixTable title="Drinks" options={drinkCols} groups={groups} field="drinks" totals={overall.drinks} />
        </>
      )}
    </div>
  );
}
